import { useState, type FormEvent } from "react";
import { Button, Dialog, ShortcutSheet, Tabs } from "@omnabase/ui";
import { api, ApiError } from "../lib/api";
import { SHORTCUTS } from "../lib/shortcuts";
import { useWs } from "../lib/nav";
import { useSession } from "./store";
import { useMediaQuery } from "../lib/inbox";
import { TOUR_MIN_WIDTH } from "../lib/tour";
import { takeTheTour } from "./Tour";

/** The "?" key and the account menu: the Help dialog (ported from the prototype's `HelpDialog.tsx`, M8 round four) with two tabs,
 *  which tab is open living in the address as ?help=shortcuts or ?help=contact. "Get help" is a message to us: it becomes a ticket
 *  in our own OmnaBase workspace (choice 6), so a person answers it in the product; the release notes tab stays out until there is a page to read. */
export const HELP_TABS = [{ id: "shortcuts", label: "Keyboard shortcuts" }, { id: "contact", label: "Get help" }] as const;
export type HelpTab = (typeof HELP_TABS)[number]["id"];
export const helpTab = (v: string | null): HelpTab | null => (HELP_TABS.some((t) => t.id === v) ? (v as HelpTab) : null);
export const helpOpen = (v: string | null) => helpTab(v) !== null;
export function HelpDialog({ tab, onTab, onClose }: { tab: HelpTab; onTab: (t: HelpTab) => void; onClose: () => void }) {
  // M11 round three: "Take the tour" on both tabs from 768 px (never on a phone); the focus goes back to where it was before Help opened
  const wide = useMediaQuery(`(min-width: ${TOUR_MIN_WIDTH}px)`);
  const [opener] = useState(() => (typeof document === "undefined" ? null : document.activeElement));
  const tour = () => { onClose(); takeTheTour(opener && opener !== document.body ? opener : null); };
  return (
    <Dialog title="Help" intro={tab === "shortcuts" ? "The inbox is built to be cleared without the mouse. Single letters work whenever you are not typing." : "A person answers. We use OmnaBase for this ourselves, so your message is a ticket in our queue."} width={960} onClose={onClose}
      footer={<><span className="ob-help__foot">{tab === "shortcuts" ? "⌘ is Ctrl on Windows and Linux." : "Documentation comes with the public launch."}</span>{wide && <Button onClick={tour}>Take the tour</Button>}<Button variant="primary" onClick={onClose}>Done</Button></>}>
      <div className="ob-help__tabs"><Tabs variant="line" items={[...HELP_TABS]} active={tab} onChange={(t) => onTab(t as HelpTab)} /></div>
      {tab === "shortcuts" ? <ShortcutSheet groups={SHORTCUTS} /> : <Contact onClose={onClose} />}
    </Dialog>
  );
}

function Contact({ onClose }: { onClose: () => void }) {
  const { slug } = useWs(); const { workspace, me, toast } = useSession();
  const [subject, setSubject] = useState(""); const [message, setMessage] = useState(""); const [busy, setBusy] = useState(false);
  const ready = subject.trim().length > 0 && message.trim().length > 0 && !busy;
  const send = async (e: FormEvent) => {
    e.preventDefault(); if (!ready) return;
    setBusy(true);
    try { const r = await api.post<{ ok: true; reference: number }>(`/api/v1/w/${slug}/help/contact`, { subject: subject.trim(), text: message.trim() }); toast(`Sent · reference #${r.reference} · we answer ${me.user.email} within one working day`, { kind: "success" }); onClose(); }
    catch (err) { toast(err instanceof ApiError ? err.message : "Could not send the message", { kind: "error" }); setBusy(false); }
  };
  return (
    <div className="ob-help__contact">
      <form className="ob-help__form" onSubmit={(e) => void send(e)}>
        <label className="ob-dialog__label" htmlFor="help-subject">What is it about?</label>
        <input id="help-subject" className="ob-dialog__input" value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} placeholder="One line" />
        <label className="ob-dialog__label" htmlFor="help-message">What happened, and what did you expect?</label>
        <textarea id="help-message" className="ob-dialog__input ob-help__message" value={message} maxLength={5000} onChange={(e) => setMessage(e.target.value)} />
        <p className="ob-dialog__hint">Sent with it: your workspace ({workspace.name}) and your address, so we can answer. Nothing from your tickets.</p>
        <div><Button variant="primary" type="submit" disabled={!ready}>Send to OmnaBase support</Button></div>
      </form>
      <ul className="ob-help__links">
        <li><a className="ob-help__link" href="mailto:support@omnabase.com"><span><strong>support@omnabase.com</strong><span>If you would rather write from your mail program</span></span></a></li>
      </ul>
    </div>
  );
}
